import { configureClientErrorReporting, createApiClient, type ApiRequestOptions } from '@frontend-shared/http'

import i18n from '../i18n'
import { loadingStore } from '../shared/loading/store'
import {
  addFeatureBreadcrumb,
  captureApiError,
  getIncidentRef,
} from '../telemetry'
import { reportBackendOffline, reportBackendOnline } from '../utils/backendStatus'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
type AuthRejectedHandler = () => void
let authRejectedHandler: AuthRejectedHandler | null = null

export const setAuthRejectedHandler = (handler: AuthRejectedHandler | null) => {
  authRejectedHandler = handler
  return () => {
    if (authRejectedHandler === handler) {
      authRejectedHandler = null
    }
  }
}

configureClientErrorReporting({
  apiBase: API_BASE,
  endpointPath: '/api/diagnostics/client-error-events',
  sourceApp: 'frontend-portal',
})

const BLOCKING_PATTERNS = [
  /\/api\/auth\/login$/,
  /\/api\/auth\/password\/(forgot|reset)$/,
  /\/api\/data-agent\/recognize$/,
  /\/api\/order-verification\/recognize$/,
  /\/api\/form\/submit$/,
  /\/api\/form\/publications\/.+\/(publish|complete|draft|edit-draft)$/,
  /\/api\/transporeon\/offer$/,
  /\/api\/ai-email\/transports\/.+\/(publish|cancel|offer)$/,
  /\/api\/trans\/publications\/.+$/,
  /\/api\/integrations\/.+$/,
  /\/api\/automations\/.+$/,
  /\/api\/company-users(?:\/.+)?$/,
  /\/api\/company\/logo$/,
  /\/api\/settings$/,
  /\/refresh$/,
]

const inferBlockingLoading = (path: string, method: string, _options: ApiRequestOptions): boolean => {
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    return false
  }
  return BLOCKING_PATTERNS.some((pattern) => pattern.test(path))
}

const client = createApiClient({
  apiBase: API_BASE,
  loadingStore,
  onNetworkOnline: reportBackendOnline,
  onNetworkOffline: reportBackendOffline,
  // Tylko 401 wylogowuje. 403 to blad domenowy (np. Trans: brak dostepu do API
  // wyszukiwania ofert) — nie moze czyscic sesji, blad trafia do UI jako ApiError.
  onAuthRejected: ({ status }) => {
    if (status === 401) {
      authRejectedHandler?.()
    }
  },
  inferBlockingLoading,
})

export const ApiError = client.ApiError

export interface PortalApiRequestOptions extends ApiRequestOptions {
  telemetryContext?: Record<string, unknown>
  /** false pomija captureApiError dla tego wywołania (np. błąd rejestrowany już po stronie backendu). */
  reportErrors?: boolean
}

const SMART_SPOT_ACTION_MATCHERS: Array<[RegExp, string]> = [
  [/^\/api\/form\/prefill$/, 'prefill'],
  [/^\/api\/form\/prefill\/enrich$/, 'enrich'],
  [/^\/api\/form\/submit$/, 'submit'],
  [/^\/api\/form\/publications\/[^/]+\/publish$/, 'publish'],
  [/^\/api\/form\/publications\/[^/]+\/draft$/, 'draft'],
  [/^\/api\/form\/publications\/[^/]+\/edit-draft$/, 'edit'],
  [/^\/api\/form\/publications\/[^/]+\/complete$/, 'complete'],
  [/^\/api\/form\/publications\/[^/]+\/join$/, 'join'],
]

const AI_EMAIL_ACTION_MATCHERS: Array<[RegExp, string]> = [
  [/^\/api\/ai-email\/transports\/[^/]+\/publish$/, 'publish'],
  [/^\/api\/ai-email\/transports\/[^/]+\/cancel$/, 'cancel'],
  [/^\/api\/ai-email\/transports\/[^/]+\/offer$/, 'offer'],
  [/^\/api\/ai-email\/refresh$/, 'refresh'],
]

const resolveSmartSpotAction = (path: string) =>
  SMART_SPOT_ACTION_MATCHERS.find(([pattern]) => pattern.test(path))?.[1] || null

const resolveAiEmailAction = (path: string, method: string) => {
  if (method === 'DELETE' && /^\/api\/ai-email\/transports\/[^/]+$/.test(path)) {
    return 'delete'
  }
  return AI_EMAIL_ACTION_MATCHERS.find(([pattern]) => pattern.test(path))?.[1] || null
}

const getErrorKey = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const payload = 'payload' in error && error.payload && typeof error.payload === 'object' ? error.payload : null
  if (!payload || typeof payload !== 'object') {
    return undefined
  }
  const errorValue = 'error' in payload ? payload.error : 'message' in payload ? payload.message : undefined
  return typeof errorValue === 'string' && errorValue.trim() ? errorValue.trim() : undefined
}

const getErrorPayloadDetails = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return null
  }
  const payload = 'payload' in error && error.payload && typeof error.payload === 'object' ? error.payload : null
  if (!payload || typeof payload !== 'object' || !('details' in payload)) {
    return null
  }
  const details = payload.details
  return details && typeof details === 'object' && !Array.isArray(details)
    ? (details as Record<string, unknown>)
    : null
}

const getErrorText = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return String(error || '')
  }
  const candidate = error as { message?: unknown; name?: unknown }
  return `${String(candidate.name || '')} ${String(candidate.message || '')}`.trim()
}

const isAbortError = (error: unknown) =>
  /aborterror|aborted|signal is aborted/i.test(getErrorText(error))

const EXPECTED_SMART_SPOT_REJECTIONS = new Set([
  'form.publications.invalidState',
  'form.publications.noExchangesConnected',
])

// Podpowiedzi miejscowości są best-effort: po limicie 4–5 s formularz działa dalej bez nich, a każde
// wywołanie Geoapify i tak trafia do geoapify_api_request_audit — w Sentry był to wyłącznie szum.
const BEST_EFFORT_PLACE_LOOKUP = /^\/api\/common\/places\/(resolve-text|autocomplete)(\?|$)/

const isTimeoutError = (error: unknown) =>
  Boolean(error) && typeof error === 'object' && (error as { name?: unknown }).name === 'TimeoutError'

const shouldIgnoreClientError = (error: unknown, feature: 'smart-spot' | 'ai-email' | null) => {
  if (!(error instanceof ApiError)) {
    return false
  }
  if (error.status === 401 || error.status === 403) {
    return true
  }
  if (feature !== 'smart-spot') {
    return false
  }
  const errorKey = getErrorKey(error)
  if (error.status === 409 && errorKey === 'form.publications.lockedByAnotherUser') {
    return true
  }
  // Oczekiwane odmowy z czytelnym komunikatem w UI: invalidState to wyścig z nieaktualną listą
  // (wiersz zdjęty przez auto-wycofanie x4 / innego dyspozytora; runAction odświeża listę po błędzie),
  // noExchangesConnected odsyła do Ustawień. `expired` celowo zostaje — patrz edycja wygasłej publikacji.
  return error.status === 400 && EXPECTED_SMART_SPOT_REJECTIONS.has(errorKey ?? '')
}

const shouldCaptureApiError = (
  error: unknown,
  feature: 'smart-spot' | 'ai-email' | null,
  path: string
) => {
  // Aborts are intentional cancellations — caller-triggered, user navigation, or
  // browser tab unload. They are never actionable, so they are never reported.
  // Genuine timeouts surface as TimeoutError (not an abort) and still get captured.
  if (isAbortError(error)) {
    return false
  }
  if (isTimeoutError(error) && BEST_EFFORT_PLACE_LOOKUP.test(path)) {
    return false
  }
  if (error instanceof ApiError) {
    if ((error.status ?? 0) >= 500) {
      return true
    }
    if ((error.status ?? 0) >= 400) {
      return Boolean(feature) && !shouldIgnoreClientError(error, feature)
    }
    return false
  }
  return true
}

export const apiRequest = async <T = unknown>(
  path: string,
  options: PortalApiRequestOptions = {}
): Promise<T> => {
  const { telemetryContext, reportErrors = true, ...requestOptions } = options
  const method = String(requestOptions.method || 'GET').toUpperCase()
  const smartSpotAction = resolveSmartSpotAction(path)
  const aiEmailAction = resolveAiEmailAction(path, method)
  const feature = smartSpotAction ? 'smart-spot' : aiEmailAction ? 'ai-email' : null
  const action = smartSpotAction || aiEmailAction
  const baseTelemetryContext = {
    ...(telemetryContext || {}),
    ...(path ? { endpoint: path } : {}),
    ...(method ? { method } : {}),
    ...(feature && action ? { feature, action } : {}),
  }

  if (smartSpotAction) {
    addFeatureBreadcrumb('Smart Spot API request started', baseTelemetryContext)
  } else if (aiEmailAction) {
    addFeatureBreadcrumb('AI Email API request started', baseTelemetryContext)
  }

  try {
    const response = await client.apiRequest<T>(path, requestOptions)
    if (smartSpotAction) {
      addFeatureBreadcrumb('Smart Spot API request succeeded', baseTelemetryContext)
    } else if (aiEmailAction) {
      addFeatureBreadcrumb('AI Email API request succeeded', baseTelemetryContext)
    }
    return response
  } catch (error) {
    const status = error instanceof ApiError ? error.status : undefined
    const errorKey = getErrorKey(error)
    const payloadDetails = getErrorPayloadDetails(error)
    const telemetryErrorContext = {
      ...baseTelemetryContext,
      ...(status != null ? { status } : {}),
      ...(errorKey ? { errorKey } : {}),
      ...(typeof payloadDetails?.validationField === 'string' ? { validationField: payloadDetails.validationField } : {}),
      ...(typeof payloadDetails?.validationReason === 'string' ? { validationReason: payloadDetails.validationReason } : {}),
    }

    if (smartSpotAction) {
      addFeatureBreadcrumb('Smart Spot API request failed', telemetryErrorContext)
    } else if (aiEmailAction) {
      addFeatureBreadcrumb('AI Email API request failed', telemetryErrorContext)
    }

    if (reportErrors && shouldCaptureApiError(error, feature, path)) {
      captureApiError(error, telemetryErrorContext)
    }

    const incidentRef = getIncidentRef(error)
    if (incidentRef && error && typeof error === 'object' && !('__incidentRef' in error)) {
      ;(error as Record<string, unknown>).__incidentRef = incidentRef
    }

    throw error
  }
}
export const clearApiCache = client.clearApiCache
export const invalidateApiCache = client.invalidateApiCache
export const getApiBase = client.getApiBase

export type CompanyUserStatus = 'active' | 'blocked' | 'pending_setup' | 'setup_link_expired'

export interface CompanyUserSummary {
  id: number
  firstName?: string | null
  lastName?: string | null
  displayName?: string | null
  email: string
  phone?: string | null
  status: CompanyUserStatus
  createdAt?: string | null
  lastLoginAt?: string | null
  isSelf: boolean
  isCompanyMaster: boolean
  canBlock: boolean
  canUnblock: boolean
  canSendPasswordLink: boolean
  statisticsModuleEnabled: boolean
}

export interface CompanyUsersResponse {
  users: CompanyUserSummary[]
}

export interface CompanyUserActionResponse {
  user: CompanyUserSummary
  deliveryStatus?: 'sent' | 'failed'
  deliveryErrorKey?: string | null
}

export interface CompanyUserCreatePayload {
  firstName: string
  lastName: string
  email: string
  phone?: string
  language?: string
}

export interface CompanyUserPhonePayload {
  phone: string
}

export function getCompanyUsers(
  { token, signal }: { token?: string | null; signal?: AbortSignal | undefined } = {}
) {
  return apiRequest<CompanyUsersResponse>('/api/company-users', {
    ...(token !== undefined ? { token } : {}),
    ...(signal ? { signal } : {}),
    cacheKey: 'company-users',
    cacheTtlMs: 5000,
    skipGlobalLoading: true,
  })
}

export type PublicationPersonView = 'published' | 'archived' | 'prepared'

export interface PublicationPerson {
  id: number
  name: string
  count: number
}

export interface PublicationPersonsResponse {
  items: PublicationPerson[]
}

/**
 * Osoby występujące w danej tabeli publikacji (z licznikami) — opcje filtru „po zaznaczonych
 * osobach". Firma bierze się z tokenu, więc endpoint jest dostępny dla każdego spedytora,
 * w odróżnieniu od master-only /api/company-users.
 */
export function getPublicationPersons(
  view: PublicationPersonView,
  { token, signal }: { token?: string | null; signal?: AbortSignal | undefined } = {}
) {
  return apiRequest<PublicationPersonsResponse>(
    `/api/form/publications/persons?view=${encodeURIComponent(view)}`,
    {
      ...(token !== undefined ? { token } : {}),
      ...(signal ? { signal } : {}),
      cacheKey: `publication-persons:${view}`,
      cacheTtlMs: 60000,
      skipGlobalLoading: true,
    }
  )
}

export interface JoinPublicationResponse {
  message?: string
  alreadyJoined?: boolean
  originId?: number
  copyId?: number
  exchanges?: string[]
}

/**
 * Podłączenie zalogowanego użytkownika do cudzego frachtu — backend tworzy jego kopię
 * publikacji i wystawia ją na wspólnych giełdach. Idempotentne: ponowne wywołanie
 * zwraca 200 z alreadyJoined (bez specjalnego 409); błędy przychodzą jako 400
 * z {error: '<klucz i18n>'} jak w pozostałych endpointach publikacji.
 */
export function joinPublication(id: string | number, token?: string | null) {
  return apiRequest<JoinPublicationResponse>(`/api/form/publications/${id}/join`, {
    method: 'POST',
    ...(token !== undefined ? { token } : {}),
  })
}

export function createCompanyUser(
  payload: CompanyUserCreatePayload,
  { token }: { token?: string | null } = {}
) {
  return apiRequest<CompanyUserActionResponse>('/api/company-users', {
    method: 'POST',
    ...(token !== undefined ? { token } : {}),
    body: payload,
    blockingLoading: true,
    loadingLabel: i18n.t('companyUsers.loading.create'),
  })
}

export function sendCompanyUserPasswordLink(
  userId: string | number,
  payload: { language?: string } = {},
  { token }: { token?: string | null } = {}
) {
  return apiRequest<CompanyUserActionResponse>(`/api/company-users/${userId}/password-link`, {
    method: 'POST',
    ...(token !== undefined ? { token } : {}),
    body: payload,
    blockingLoading: true,
    loadingLabel: i18n.t('companyUsers.loading.passwordLink'),
  })
}

export function blockCompanyUser(userId: string | number, { token }: { token?: string | null } = {}) {
  return apiRequest<CompanyUserActionResponse>(`/api/company-users/${userId}/block`, {
    method: 'POST',
    ...(token !== undefined ? { token } : {}),
    blockingLoading: true,
    loadingLabel: i18n.t('companyUsers.loading.block'),
  })
}

export function unblockCompanyUser(userId: string | number, { token }: { token?: string | null } = {}) {
  return apiRequest<CompanyUserActionResponse>(`/api/company-users/${userId}/unblock`, {
    method: 'POST',
    ...(token !== undefined ? { token } : {}),
    blockingLoading: true,
    loadingLabel: i18n.t('companyUsers.loading.unblock'),
  })
}

export function updateCompanyUserPhone(
  userId: string | number,
  payload: CompanyUserPhonePayload,
  { token }: { token?: string | null } = {}
) {
  return apiRequest<CompanyUserActionResponse>(`/api/company-users/${userId}/phone`, {
    method: 'PUT',
    ...(token !== undefined ? { token } : {}),
    body: payload,
    blockingLoading: true,
    loadingLabel: i18n.t('companyUsers.loading.phone'),
  })
}

export function setCompanyUserStatisticsModule(
  userId: string | number,
  payload: { enabled: boolean },
  { token }: { token?: string | null } = {}
) {
  return apiRequest<CompanyUserActionResponse>(`/api/company-users/${userId}/statistics-module`, {
    method: 'PUT',
    ...(token !== undefined ? { token } : {}),
    body: payload,
    blockingLoading: true,
    loadingLabel: i18n.t('companyUsers.loading.statistics'),
  })
}
